import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {
   TscThemeName,
   TscThemeNames,
   TscStyles
} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import CssUtil from '../../util/CssUtil';
import {useTscTheme} from '../../context/themeProvider';
import cssConstants from '../../constants/cssConstants';
import WhatsNewUpdateGroup from './WhatsNewUpdateGroup';
import config from '../../service/config';

interface IWhatsNewUIColors {
   textColor: string;
   contentBg: string;
   wrapperBg: string;
   border: string;
   discalimerBoxShadow: string;
}

const WhatsNewUIThemes = {
   [TscThemeName.dawn]: {
      textColor: TscStyles.color.text.mediumDark,
      contentBg: TscStyles.color.ui.dawn.lightMedium,
      wrapperBg: TscStyles.color.ui.dawn.light,
      border: 'rgba(0,0,0,.05) 1px solid',
      discalimerBoxShadow: 'rgba(255,255,255,1) 0px 0px 5px inset, rgb(0 0 0 / 5%) 0px 1px 3px'
   } as IWhatsNewUIColors,
   [TscThemeName.dusk]: {
      textColor: TscStyles.color.text.light,
      contentBg: TscStyles.color.ui.dusk.medium,
      wrapperBg: TscStyles.color.ui.dusk.mediumDark,
      border: 'rgb(255 255 255 / 5%) 1px solid',
      discalimerBoxShadow: 'rgba(255,255,255,.01) 0px 0px 5px inset, rgb(0 0 0 / 10%) 0px 1px 3px'
   } as IWhatsNewUIColors
};

interface IWhatsNewStyledComponentProps {
   theme: IWhatsNewUIColors;
}

const WhatsNewPane = styled.div<{theme: IWhatsNewUIColors}>`
   box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   font-family: sans-serif;
   display: flex;
   background-color: ${(props: IWhatsNewStyledComponentProps) => props.theme.wrapperBg};
   border-radius: ${TscStyles.border.radius.xl};
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   margin: 1rem auto;
   padding-bottom: 1rem;
   width: 100%;
   max-width: 54rem;
   height: fit-content;

   @media screen and (max-width: 896px) {
      margin-left: 1rem;
      margin-right: 1rem;
   }

   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      margin:0;
      height: fit-content;
   }
`;

const ContentWrapper = styled.div<{theme: IWhatsNewUIColors}>`
   font-family: ${TscStyles.font.family.primary};
   display: flex;
   flex-direction: column;
   margin: auto;
   width: 100%;
   color: ${(props: IWhatsNewStyledComponentProps) => props.theme.textColor};
`;

const WhatsNewContentWrapper = styled.div`
   flex-direction: column;
   overflow: auto;
   margin: 0 2rem;
   display: flex;
   grid-template-columns: 1fr 1fr;
   align-items: center;
   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      margin: 0 1rem;
   };
`;

const WhatsNewContainer = styled.h2`
   font-weight: 400;
   font-size: 2.25rem;
   line-height: 1.875rem;
   margin: 2rem 0 0.75rem 0;
   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      font-size: 2rem;
      margin: 1.5rem 0 0.75rem 0;
   };
`;

const ContentBorder = styled.div`
   z-index: ${cssConstants.zIndex.viewPageElement};
   height: .1875rem;
   width: 100%;
   border-radius: ${TscStyles.border.radius.xs};
   radius: ${TscStyles.border.radius.xs};
   background: ${cssConstants.brandGradient};
`;

const ProIcon = styled.a`
   padding: .5rem 1.25rem;
   border-radius: 24px;
   gap: .5rem;
   line-height: 1rem;
   height: fit-content;
   font-size: .875rem;
   text-decoration: none;
   color: ${TscStyles.color.text.mediumDark};
   font-weight: 600;
   margin-left: 0.5rem;
   background: linear-gradient(90deg, ${CssUtil.toRgba(TscStyles.color.action.screencastPrimaryStop1, 1)}, ${CssUtil.toRgba(TscStyles.color.action.screencastPrimaryStop2, 1)});

   > a {
      text-decoration: none;
      color: inherit;
   }
`;

const UpdateList = styled.ul<{theme: IWhatsNewUIColors}>`
   margin-top: 0.5rem;
   align-items: center;
   padding: 1rem;
   border-radius: ${TscStyles.border.radius.lg};
   list-style-position: inside;
   background-color: ${(props: IWhatsNewStyledComponentProps) => props.theme.contentBg};
   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      padding: .875rem;
      margin: .5rem 0;
   };
`;

const UpdateListItem = styled.li<{isProUpdate: boolean}>`
   font-size: 1rem;
   line-height: 1.5rem;
   font-weight: ${props => props.isProUpdate ? '700' : 'normal'};
   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      font-size: .875rem;
   };
`;

const DisclaimerContainer = styled.div<{theme: IWhatsNewUIColors}>`
   display: flex;
   flex-direction: row;
   margin-top: 1rem;
   padding: .5rem;
   border-radius: 32px;
   font-weight: 600;
   justify-content: center;
   align-items: center;
   width: 100%;
   box-sizing: border-box;
   color: ${(props: IWhatsNewStyledComponentProps) => props.theme.textColor};
   background-color: ${(props: IWhatsNewStyledComponentProps) => props.theme.contentBg};
   border: ${(props: IWhatsNewStyledComponentProps) => props.theme.border};
   box-shadow: ${(props: IWhatsNewStyledComponentProps) => props.theme.discalimerBoxShadow};

   @media screen and (max-width: ${cssConstants.whatsNewMobileBreakpoint}px) {
      font-size: .875rem;
   };
`;

const UpdateLink = styled.a`
   color: ${cssConstants.alertBarLinkTextColor};
`;

const LearnMoreLInk = styled.a`
   color: ${cssConstants.alertBarLinkTextColor};
   font-weight: normal;
`;

const Disclaimer: React.FC<{theme: TscThemeNames; children?: React.ReactNode | React.ReactNode[]}> = ({theme, children}) => (
   <DisclaimerContainer theme={WhatsNewUIThemes[theme]}>
      {children}
   </DisclaimerContainer>
);

export const WhatsNewViewBase: React.FC = () => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   return (
      <WhatsNewPane
         data-test-id="whats-new-pane"
         theme={WhatsNewUIThemes[theme]}
      >
         <ContentWrapper theme={WhatsNewUIThemes[theme]}>
            <WhatsNewContentWrapper>

               <WhatsNewContainer>
                  {t('whatsNew.title')}
               </WhatsNewContainer>
               <ContentBorder />

               <Disclaimer theme={theme}>
                  {t('whatsNew.updateDisclaimer')}
                  <ProIcon
                     target="_blank"
                     href={config.environmentData.ScreencastProPurchaseLinkUrl}
                     rel="noreferrer"
                  >
                     {t('whatsNew.getPro')}
                  </ProIcon>
               </Disclaimer>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="January 13th, 2025"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Updated design of media view page
                     </UpdateListItem>
                     <UpdateListItem isProUpdate={false}>
                        Added transcript view for videos with captions
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="November 25th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Media uploads from mobile devices are now supported
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="November 12th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Added a Recent option to filter your library and show all recently uploaded media
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="August 14th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Fixed issue with Screencast sign up and email address verification
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="May 23rd, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={true}>
                        Reduced time to generate a transcription
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="March 21st, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Next and Previous options on media pages in collections for easier navigation
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="March 8th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Annotations on a scrollable image are moved into view when a user selects that comment
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="March 6th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Combined collection sharing and management options into a single menu
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="February 14th, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Search feature that finds media in your library by title or description
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="January 22, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Option to view media in your library as a list for easier scanning and content management
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="January 11, 2024"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Moved the Feed tab to the left navigation under Collections and renamed it Recently Added
                     </UpdateListItem>
                     <UpdateListItem isProUpdate={false}>
                        Added any collections a user follows to the Collections list
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="December 18, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        Launched What&apos;s New page
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="December 13, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={true}>
                        Removed 250 video limit - Pro users now enjoy unlimited uploads (subject to
                        <UpdateLink
                           href="https://www.techsmith.com/screencast-tos.html"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Terms of Service
                        </UpdateLink>
                        )
                     </UpdateListItem>
                     <UpdateListItem isProUpdate={false}>
                        Bug fix - Collections with many members now load faster
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="November 28, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={true}>
                        AI-generated video chapters (Beta) -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/video-toc/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="November 13, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        New interface to manually add video chapters -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/video-toc/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                     <UpdateListItem isProUpdate={true}>
                        AI-generated video titles and descriptions (Beta) -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/ai-generation-screencast/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="October 12, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={true}>
                        AI-generated video captions -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/generate-captions/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

               <WhatsNewUpdateGroup
                  theme={theme}
                  date="August 8, 2023"
               >
                  <UpdateList theme={WhatsNewUIThemes[theme]}>
                     <UpdateListItem isProUpdate={false}>
                        New Settings page to manage notifications and other preferences -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/screencast-settings/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                     <UpdateListItem isProUpdate={true}>
                        Custom logo option moved to the Settings page -
                        <LearnMoreLInk
                           href="https://www.techsmith.com/learn/tutorials/screencast/custom-branding/"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Learn more
                        </LearnMoreLInk>
                     </UpdateListItem>
                  </UpdateList>
               </WhatsNewUpdateGroup>

            </WhatsNewContentWrapper>
         </ContentWrapper>
      </WhatsNewPane>
   );
};

export default memo(WhatsNewViewBase);
